import type { MenuItemProps } from '@chakra-ui/react'
import { Box, Input, Group, Stack, StackSeparator, VisuallyHidden } from '@chakra-ui/react'
import type { FC, FormEventHandler } from 'react'
import { useCallback, useState } from 'react'

import { Button } from '@app/components/ui/button'
import type { ButtonProps } from '@app/components/ui/button'
import {
  DialogBody,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@app/components/ui/dialog'
import { Field } from '@app/components/ui/field'
import { FileUploadDropzone, FileUploadRoot, FileUploadList } from '@app/components/ui/file-upload'
import { toaster } from '@app/components/ui/toaster'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import useTrack from '@app/hooks/useTrack'
import ToggleInput from '@app/next/forms/toggleInput'
import UppyRoot from '@app/next/forms/uppyRoot'
import Preview from '@app/pages/metrics/components/csv/preview'
import { useStore } from '@app/store'
import { MetricUploadBatchFile, MetricUploadFile } from '@graphql/documents/metric.graphql'
import type { MetricUploadFileMutationVariables } from '@graphql/queries'
import type { CsvConfigurationSegmentInput, Metric } from '@graphql/types'

// TODO: hardcoding both prop types is dirty. This could be worked around with
//       separate modals, or a bunch of conditionals.
type Props<OProps = ButtonProps | MenuItemProps> = OProps & {
  open: boolean
  setOpen: (open: boolean) => void
  metric?: Pick<Metric, 'id'> | null
}

const MetricCSVModal: FC<Props> = ({ open, setOpen, metric = null }) => {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState<File | null>(null)

  const [shrineHash, setShrineHash] = useState<string | null>(null)
  const { user } = useStoreCurrentUser()
  const actionMutation = useStore.use.actionMutation()
  const track = useTrack()

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault()

      const formData = new FormData(e.currentTarget)
      const values = Object.fromEntries(formData.entries())
      const {
        clearExistingData,
        shrineHash: inputShrineHash,
        headers,
        dateIndex,
        valueIndex,
        idIndex,
        segments
      } = values

      let input: Partial<MetricUploadFileMutationVariables['input']> = {
        clearExistingData: clearExistingData as string,
        headers: headers,
        shrineHash: inputShrineHash as string,
        dateIndex: Number(dateIndex),
        valueIndex: Number(valueIndex),
        idIndex: Number(idIndex),
        segments: segments ? JSON.parse(segments as string) : ([] as CsvConfigurationSegmentInput[])
      }
      let mutation = MetricUploadBatchFile

      if (metric?.id) {
        input = { ...input, metricId: metric.id }
        delete input.idIndex
        mutation = MetricUploadFile
      }

      try {
        await actionMutation<typeof mutation>(mutation, input)
        setShrineHash(null)
        setFile(null)

        setOpen(false)

        toaster.create({
          title: 'CSV Upload Successful!',
          description:
            'We will begin processing the file soon. Check a metric page after a few minutes to see the results.',
          type: 'success'
        })
      } catch (err) {
        setError(err.message)
      }
    },
    [actionMutation, metric?.id, setOpen]
  )

  if (!user) {
    return null
  }

  return (
    <DialogRoot
      size="lg"
      onOpenChange={(e) => {
        setFile(null)
        setOpen(e.open)
      }}
      open={open}
      placement="center"
      lazyMount
    >
      <DialogBackdrop />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload metric data via CSV</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <form id="metric-csv-form" onSubmit={onSubmit}>
          <DialogBody>
            <Stack gap={4} separator={<StackSeparator />}>
              <UppyRoot
                fileTypes={['text/csv']}
                onUploadStart={() => {
                  setFile(null)
                  setLoading(true)
                }}
                onUploadSuccess={(newShrineHash, uploadedFile) => {
                  setShrineHash(newShrineHash)
                  setLoading(false)
                  track('csv:succeed', { filename: uploadedFile.name })
                  setFile(uploadedFile.data)
                }}
                onUploadError={(uploadedFile) => {
                  setLoading(false)
                  track('csv:fail', { filename: uploadedFile?.name })
                }}
              >
                {({ shrineHash: csvShrineHash, onFileAccept }) => (
                  <Field label="CSV" required invalid={!!error} errorText={error}>
                    <FileUploadRoot
                      gap={1}
                      onFileAccept={onFileAccept}
                      maxW="100%"
                      alignItems="stretch"
                      maxFiles={1}
                      accept={['text/csv']}
                    >
                      <VisuallyHidden asChild>
                        <Input name="shrineHash" readOnly value={csvShrineHash} />
                      </VisuallyHidden>
                      {!file && (
                        <FileUploadDropzone
                          label="Drag and drop or click here to upload a CSV"
                          description=".csv up to 50MB"
                        />
                      )}
                      <FileUploadList />
                    </FileUploadRoot>
                  </Field>
                )}
              </UppyRoot>
              <Preview file={file} requireIdField={!metric} />
              <Box css={{ '--field-label-width': '220px' }}>
                <Field label="Clear existing data points?" orientation="horizontal">
                  <ToggleInput name="clearExistingData" defaultValue={false} />
                </Field>
              </Box>
            </Stack>
          </DialogBody>

          <DialogFooter>
            <Group>
              <Button
                onClick={() => {
                  setOpen(false)
                  setFile(null)
                  setShrineHash(null)
                }}
                variant="outline"
              >
                Close
              </Button>
              <Button loading={loading} disabled={!shrineHash} type="submit">
                Upload
              </Button>
            </Group>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  )
}

export default MetricCSVModal
